'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Colors, Spacing } from '@lichtblick/theme';
import { useTracker } from '@lichtblick/tracker';
import { Box, Tabs } from '@lichtblick/ui-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AButton } from '../AButton';
import { MTariffCard } from '../MTariffCard';
import { MTextIntro } from '../MTextIntro';
import { AnchorMark, GridColumn, GridContainer, GridRow } from '../shared';
import { ComparisonCard, ComparisonCards, ComparisonFooter, GridRowExtend, StyledText } from './OTariffComparison.styles';
const CARDS_INTERSECTION_THRESHOLD = 0.3;
const buildTrackingObject = (card, index)=>({
        item_id: new URLSearchParams(card.button?.[0]?.linkUrl?.split('?')[1]).get('productCode') ?? '',
        item_name: card.headline,
        item_category: 'B2C E-Mob',
        index: index + 1,
        item_list_name: 'B2C E-Mob',
        price: 0
    });
const OTariffComparison = ({ accent, anchorId, button, cards, footnote, textIntro })=>{
    const { isVirtPathTracked, trackCustomEvent, trackEcommerceEvent } = useTracker();
    const ref = useRef(null);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const trackClickEvent = useCallback((card, index)=>{
        if (card?.button?.[0]?.trackingOption?.[0]?.eventName) {
            const trackingOption = card.button[0].trackingOption[0];
            trackCustomEvent({
                eventName: trackingOption.eventName,
                customProps: trackingOption.customProps && JSON.parse(trackingOption.customProps)
            });
        }
        trackEcommerceEvent({
            event: 'Ecommerce - Add to cart',
            event_name: 'add_to_cart',
            add_to_cart: {
                bundle_intention: false,
                currency: 'EUR',
                items: [
                    buildTrackingObject(card, index)
                ]
            }
        });
    }, [
        trackCustomEvent,
        trackEcommerceEvent
    ]);
    const renderTabContent = useCallback((items)=>{
        return /*#__PURE__*/ _jsx(ComparisonCards, {
            children: items?.map((item, index)=>/*#__PURE__*/ _jsx(ComparisonCard, {
                    children: /*#__PURE__*/ _jsx(MTariffCard, {
                        ...item,
                        onClick: ()=>trackClickEvent(item, index)
                    })
                }, item._uid))
        });
    }, [
        trackClickEvent
    ]);
    const tabs = useMemo(()=>{
        const cardsWithContract = cards?.filter((item)=>{
            return item.withEnergyContract === true;
        });
        const cardsWithoutContract = cards?.filter((item)=>{
            return item.withEnergyContract === false;
        });
        if (cardsWithContract && cardsWithoutContract && cardsWithContract?.length > 0 && cardsWithoutContract?.length > 0) {
            const tabsWithContract = renderTabContent(cardsWithContract);
            const tabsWithoutContract = renderTabContent(cardsWithoutContract);
            return [
                {
                    headline: 'Neukund*in',
                    contentSlot: tabsWithoutContract
                },
                {
                    headline: 'Bestandskund*in',
                    contentSlot: tabsWithContract
                }
            ];
        }
    }, [
        cards,
        renderTabContent
    ]);
    useEffect(()=>{
        const intersectionObserver = new IntersectionObserver(([entry])=>setIsIntersecting(entry.intersectionRatio >= CARDS_INTERSECTION_THRESHOLD), {
            threshold: CARDS_INTERSECTION_THRESHOLD
        });
        const comparisonCards = ref.current;
        if (comparisonCards) {
            intersectionObserver.observe(comparisonCards);
        }
        return ()=>{
            intersectionObserver.disconnect();
        };
    }, []);
    useEffect(()=>{
        if (isIntersecting && isVirtPathTracked) {
            trackEcommerceEvent({
                event: 'Ecommerce - Item List Views',
                event_name: 'view_item_list',
                view_item_list: {
                    currency: 'EUR',
                    bundle_intention: false,
                    items: cards?.map(buildTrackingObject) ?? []
                }
            });
        }
    }, [
        cards,
        isIntersecting,
        isVirtPathTracked,
        trackEcommerceEvent
    ]);
    return /*#__PURE__*/ _jsxs(Box, {
        backgroundColor: Colors.FadedGray,
        "data-testid": "tariff-comparison",
        pyd: Spacing.Xl,
        pym: Spacing.M,
        children: [
            anchorId && /*#__PURE__*/ _jsx(AnchorMark, {
                anchorId: anchorId
            }),
            /*#__PURE__*/ _jsxs(GridContainer, {
                children: [
                    textIntro?.length === 1 && /*#__PURE__*/ _jsx(GridRowExtend, {
                        children: /*#__PURE__*/ _jsx(GridColumn, {
                            $push: 2,
                            $width: 8,
                            children: /*#__PURE__*/ _jsx(MTextIntro, {
                                ...textIntro[0],
                                accentColor: accent || undefined,
                                centeredDesktop: true,
                                centeredMobile: true
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridRow, {
                        children: /*#__PURE__*/ _jsx(GridColumn, {
                            $push: 2,
                            $width: 8,
                            children: tabs ? /*#__PURE__*/ _jsx(Tabs, {
                                gap: Spacing.M,
                                mobileNavAlignment: "column",
                                tabs: tabs
                            }) : /*#__PURE__*/ _jsx(ComparisonCards, {
                                ref: ref,
                                children: cards?.map((item, index)=>/*#__PURE__*/ _jsx(ComparisonCard, {
                                        children: /*#__PURE__*/ _jsx(MTariffCard, {
                                            ...item,
                                            onClick: ()=>trackClickEvent(item, index)
                                        })
                                    }, item._uid))
                            })
                        })
                    }),
                    (button || footnote) && /*#__PURE__*/ _jsx(GridRow, {
                        children: /*#__PURE__*/ _jsxs(ComparisonFooter, {
                            children: [
                                button?.length === 1 && /*#__PURE__*/ _jsx(AButton, {
                                    ...button[0]
                                }),
                                footnote && /*#__PURE__*/ _jsx(StyledText, {
                                    renderAs: "small",
                                    size: "xs",
                                    children: footnote
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
};
export default OTariffComparison;

//# sourceMappingURL=OTariffComparison.js.map